import { useEffect } from 'react';
import useApi from './useApi';
import { useRotationStore } from '../context/RotationContext';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { createPortNameList } from 'src/factory/Stowage/Port/PortNameFactory';
import { PortNameBM } from 'src/types/BusinessModel/Stowage/Port/PortNameBM';
import { BerthNameBM } from 'src/types/BusinessModel/Stowage/Port/BerthNameBM';
import { createBerthNameList } from 'src/factory/Stowage/Port/BerthNameFactory';
import { createProductList } from 'src/factory/Stowage/Product/ProductFactory';
import { createProductCategoryList } from 'src/factory/Stowage/Product/ProductCategoryFactory';
import { ProductBM } from 'src/types/BusinessModel/Stowage/Product/ProductBM';
import { ProductCategoryBM } from 'src/types/BusinessModel/Stowage/Product/ProductCategoryBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { CoatingBM } from 'src/types/BusinessModel/Stowage/Product/CoatingBM';
import { createCoatingList } from 'src/factory/Vessel/CoatingFactory';
import { CoatingCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/CoatingCompatibilitiesBM';
import { createProductCoatingCompatibilities } from 'src/factory/Stowage/Product/ProductCoatingCompatibilityFactory';
import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { useRotationResponseHandler } from './responseHandlers/useRotationResponseHandler';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, getApiData } = useApi();
    const { setPortNameList, setBerthNameList, setProductList, setProductCategoryList, setCoatingList, setUscgCompatibilities } = useRotationStore();
    const { onGetImosRotation } = useRotationResponseHandler();

    const getImosRotation = async (imoNumber: number) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getRotation'].url,
            queryParam: buildQueryParams({ imoNumber }),
        });

        if (result) {
            onGetImosRotation(result);
        }
    };

    const getPortNames = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getPortNames'].url,
        });

        if (result) {
            const portNameBMs = result as PortNameBM[];
            const portNameList = createPortNameList(portNameBMs);

            setPortNameList([...portNameList]);
            return portNameList;
        }
    };

    const getBerthNames = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getBerthNames'].url,
        });

        if (result) {
            const berthNameBMs = result as BerthNameBM[];
            let berthNameList = createBerthNameList(berthNameBMs);
            berthNameList = berthNameList.filter((b) => b.isDeleted === false);

            setBerthNameList([...berthNameList]);
            return berthNameList;
        }
    };

    const getProducts = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getProducts'].url,
        });

        if (result) {
            const productBMs = result as ProductBM[];
            const productList = createProductList(productBMs);

            setProductList([...productList]);
            return productList;
        }
    };

    const getProductCategories = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getProductCategories'].url,
        });

        if (result) {
            const productCategoryBMs = result as ProductCategoryBM[];
            const productCategoryList = createProductCategoryList(productCategoryBMs);

            setProductCategoryList([...productCategoryList]);
            return productCategoryList;
        }
    };

    const getCoatings = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getCoatings'].url,
        });

        if (result) {
            const coatingBMs = result as CoatingBM[];
            const coatingList = createCoatingList(coatingBMs);

            setCoatingList([...coatingList]);
            return coatingList;
        }
    };

    const getCoatingCompatibilities = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getCoatingCompatibilities'].url,
        });

        if (result) {
            const coatingCompatibilityBMs = result as CoatingCompatibilitiesBM;
            const coatingCompatibilities = createProductCoatingCompatibilities(coatingCompatibilityBMs?.coatingCompatibilities ?? []);

            return coatingCompatibilities;
        }
    };

    const getUscgCompatibilities = async () => {
        const result = await getApiData({
            endpoint: apiEndpoints['getUscgCompatibilities'].url,
        });

        if (result) {
            const uscgCompatibilitiesBMs = result as UscgCompatibilitiesBM;

            setUscgCompatibilities({ ...uscgCompatibilitiesBMs });
            return uscgCompatibilitiesBMs;
        }
    };

    useEffect(() => {
        processApiEndpointError(state);
    }, [state]);

    return {
        state,
        getImosRotation,
        getPortNames,
        getBerthNames,
        getProducts,
        getProductCategories,
        getCoatings,
        getCoatingCompatibilities,
        getUscgCompatibilities,
    };
};
